import { languageSortFilterModule } from '../../filters/language-sort';
import { tooltipComponentModule } from '../tooltip/tooltip';
import languagePickerTemplate from './language-picker.tpl.html';
import languagePickerDropdownTemplate from './language-picker-dropdown.tpl.html';
import './language-picker.less';
import { languages } from '../../constants/languages';

/**
 * @name Language-Picker
 * @docType shared
 * @category directive
 * @description
 * A directive that allows you to pick between languages
 * @example
 *
 * <h2>Default Language Picker</h2>
 * <p>Select one item from the list of all languages</p></br>
 * <example>
 * <language-picker>
 * </language-picker>
 * </example>
 *
 * <h2>Disabled Example</h2>
 * <p>just try and click one... I dare you!</p></br>
 * <example>
 * <language-picker disabled="true" languages="['javascript', 'ruby']" active-watch="['ruby']">
 * </language-picker>
 * </example>
 *
 * <h2>Multiple Language Picker</h2>
 * <p>Select multiple items from a list of pre-determined languages, with a requirement of one language</p></br>
 * <example>
 * <language-picker multi="true"
 *  require-one="true"
 *  languages="['javascript', 'ruby', 'coffeescript', 'python']"
 *  active-watch="['javascript', 'coffeescript']"></language-picker>
 * </example>
 *
 * <h2>Required Languages</h2>
 * <p>Additional restrictions are also supported:</p></br>
 * <example>
 * <language-picker multi="true"
 *  require-one="true"
 *  languages="['javascript', 'python', 'haskell', 'ruby']"
 *  active-watch="['javascript', 'ruby', 'python']"
 *  required-languages="{javascript: 'JavaScript is required because its the bomb!', ruby: 'ruby is pretty cool too' }"></language-picker>
 *  </example>
 * @returns Directive Definition Object {object}
 */
export const languagePickerComponentModule = angular
  .module('Q.components.language-picker', [
    languageSortFilterModule,
    tooltipComponentModule,
  ])
  .directive('languagePicker', function () {
    return {
      scope: {
        languages: '<', // if not provided, defaults to all supported languages
        multi: '<', // is this a multi-select? defaults to false
        onChange: '&', // the event that occurs when a language is select or un-selected. For multi-select it will send an array of languages, for single-select it will return a single language
        active: '=?ngModel',
        activeWatch: '=', // a model to bind the active language(s) to
        inactiveWatch: '=',
        requireOne: '=', // is it required that one be selected at all times? Available for multi-select
        disabled: '<',
        requiredLanguages: '=',
        primaryCompare: '<',
        listStyle: '<',
      },
      require: ['?ngModel', 'languagePicker'],
      replace: true,
      template: languagePickerTemplate,
      controller: function ($scope, $timeout) {
        let ngModel;
        this.setNgModel = (ngm) => {
          ngModel = ngm;
          ngModel.$render = () => resetValidity();
          resetValidity();
        };

        function resetValidity() {
          if ($scope.requireOne) {
            let viewValue = ngModel.$viewValue;
            if (viewValue && viewValue.length > 0) {
              ngModel.$setValidity('requireOne', null);
            } else {
              ngModel.$setValidity('requireOne', false);
            }
          }
        }

        $scope.availableLanguages =
          $scope.languages || languages.all.map((l) => l.id);
        $scope.active = $scope.active || [];
        $scope.isActive = (lang) =>
          $scope.active && $scope.active.indexOf(lang) !== -1;
        $scope.$watch('activeWatch', function (newValue) {
          if (newValue) {
            $scope.active = angular.isArray(newValue) ? newValue : [newValue];
          }
        });
        $scope.$watch('inactiveWatch', function (newValue) {
          if (newValue) {
            const inactives = angular.isArray(newValue) ? newValue : [newValue];
            $scope.active = $scope.availableLanguages.filter(
              (l) => inactives.indexOf(l) === -1,
            );
          }
        });

        let timer;

        function showError(msg) {
          $scope.errorMsg = msg;
          $timeout.cancel(timer);
          timer = $timeout(function () {
            delete $scope.errorMsg;
          }, 2000);
        }

        // a case-insensitive object search
        function ciObjectSearch(obj, search) {
          for (let prop in obj) {
            if (obj.hasOwnProperty(prop)) {
              if (prop.toLowerCase() === search.toLowerCase()) {
                return obj[prop];
              }
            }
          }
        }

        $scope.getTitle = (lang) => languages[lang].name;
        $scope.getListTitle = (lang) => languages[lang].namePlain;
        $scope.isPrimary = (lang) => languages[lang].primary;

        $scope.select = function (language) {
          if ($scope.disabled) {
            return;
          }
          if ($scope.multi) {
            $scope.active = $scope.active || [];
            const index = $scope.active.indexOf(language);
            if (index === -1) {
              // selecting a language
              $scope.active.push(language);
            } else {
              // unselecting a language
              if (
                $scope.requiredLanguages &&
                ciObjectSearch($scope.requiredLanguages, language)
              ) {
                // This language is required
                showError(ciObjectSearch($scope.requiredLanguages, language));
              } else if ($scope.requireOne && $scope.active.length === 1) {
                // one active language is required
                showError('At least one language is required');
              } else {
                $scope.active.splice(index, 1);
              }
            }
            $scope.onChange({
              activeLanguages: $scope.active,
              inactiveLanguages: $scope.availableLanguages.filter(
                (l) => $scope.active.indexOf(l) === -1,
              ),
            });
          } else {
            $scope.active = [language];
            $scope.onChange({ language: language });
          }
          if (ngModel) {
            ngModel.$setViewValue($scope.active.slice());
            resetValidity();
          }
        };
      },
      link(scope, elem, attr, [ngModelCtrl, languagePicker]) {
        if (ngModelCtrl) {
          languagePicker.setNgModel(ngModelCtrl);
        }
      },
    };
  })

  .component('languagePickerDropdown', {
    bindings: {
      onChange: '&',
      btnSize: '@',
      languages: '<',
    },
    require: {
      ngModel: 'ngModel',
    },
    template: languagePickerDropdownTemplate,
    controller: function () {
      const ctrl = this;
      ctrl.toName = (lang, index) => {
        let n = '';
        if (ctrl.active.length < 3) {
          n = languages.toName(lang);
          if (index < ctrl.active.length - 1) {
            n += ',';
          }
        }
        return n;
      };
      ctrl.$onInit = () => {
        ctrl.active = ctrl.ngModel.$viewValue || [];
        ctrl.ngModel.$render = () => {
          ctrl.active = ctrl.ngModel.$viewValue || [];
          ctrl.onChange();
        };
      };
      ctrl.activeChanged = () => {
        ctrl.ngModel.$setViewValue(ctrl.active.slice());
        ctrl.onChange();
      };
      ctrl.clear = () => {
        ctrl.active = [];
        ctrl.activeChanged();
        ctrl.open = false;
      };
    },
  });
